import PropTypes from 'prop-types';
import { Stack, Button, Tooltip, Typography, IconButton, ToggleButton, Grid, Autocomplete, Box, TextField } from '@mui/material';
import { fDate } from '../../utils/formatTime';
import Iconify from '../iconify';
import { useDispatch, useSelector } from '../../redux/store';
import { onOpenModal } from '../../redux/slices/vendors';

const VIEW_OPTIONS = [
  { value: 'dayGridMonth', label: 'Month', icon: 'ic:round-view-module' },
  { value: 'listYear', label: 'List', icon: 'ic:round-view-agenda' },
];

CalendarToolbar.propTypes = {
  onToday: PropTypes.func,
  onNextDate: PropTypes.func,
  onPrevDate: PropTypes.func,
  onChangeView: PropTypes.func,
  date: PropTypes.instanceOf(Date),
  view: PropTypes.oneOf(['dayGridMonth', 'timeGridWeek', 'timeGridDay', 'listWeek', 'listYear']),
  vendor: PropTypes.object,
  facility: PropTypes.object,
  setVendor: PropTypes.func,
  setFacility: PropTypes.func,
};

export default function CalendarToolbar({ date, view, onToday, onNextDate, onPrevDate, onChangeView, vendor, setVendor, facility, setFacility }) {
  const dispatch = useDispatch();
  const { data: { vendors, facilities } } = useSelector((state) => state.vendors);
  const handleOpenModal = () => dispatch(onOpenModal());
  const monthCalendar = view === 'dayGridMonth';

  return <>
    {monthCalendar && <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" spacing={1} sx={{ ml: 1, pt: 1 }}>
      <Grid item>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={() => window.open(`/Api/Vendor/DownloadVendorDates`, '_blank')}
          startIcon={<Iconify icon="file-icons:microsoft-excel" />}
        >
          Download All
        </Button>
      </Grid>
      <Grid item>
        <Typography variant="subtitle1">
          Vendor
        </Typography>
        <Autocomplete
          size="small"
          value={vendor?.label || null}
          onChange={(_, newValue) => setVendor(newValue)}
          options={vendors.map((vendor) => ({ id: vendor.id, label: vendor.name }))}
          isOptionEqualToValue={(option, value) => option?.label === value}
          renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off" />}
          sx={{
            width: '20vw',
            '& legend': { display: 'none' },
            '& fieldset': { top: 0 },
            '& .MuiFormLabel-root ': { display: 'none' },
          }}
        />
      </Grid>
      <Grid item>
        <Typography variant="subtitle1">
          Facility
        </Typography>
        <Autocomplete
          size="small"
          value={facility?.label || null}
          onChange={(_, newValue) => setFacility(newValue)}
          options={[
            { id: 'all-facilities', label: 'All Facilities' },
            ...facilities.map((facility) => ({ id: facility.value, label: facility.label }))
          ]}
          isOptionEqualToValue={(option, value) => option?.label === value}
          renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off" />}
          sx={{
            width: '20vw',
            '& legend': { display: 'none' },
            '& fieldset': { top: 0 },
            '& .MuiFormLabel-root ': { display: 'none' },
          }}
        />
      </Grid>
    </Grid>}
    <Stack alignItems="center" justifyContent="space-between" direction={{ xs: 'column', sm: 'row' }} sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1}>
        {VIEW_OPTIONS.map((viewOption) => (
          <Tooltip key={viewOption.value} title={viewOption.label}>
            <ToggleButton
              size="small"
              value={view}
              selected={viewOption.value === view}
              onChange={() => onChangeView(viewOption.value)}
            >
              <Iconify icon={viewOption.icon} />
            </ToggleButton>
          </Tooltip>
        ))}
      </Stack>

      {monthCalendar ? <Stack direction="row" alignItems="center" spacing={2}>
        <IconButton onClick={onPrevDate}>
          <Iconify icon="eva:arrow-ios-back-fill" />
        </IconButton>

        <Typography variant="h5">{fDate(date)}</Typography>

        <IconButton onClick={onNextDate}>
          <Iconify icon="eva:arrow-ios-forward-fill" />
        </IconButton>
      </Stack> : <Typography variant="h5">All Ship Days</Typography>}

      <Stack direction="row" alignItems="center" spacing={1}>
        {monthCalendar && <Button size="small" color="info" variant="contained" onClick={onToday}>
          Today
        </Button>}
        <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleOpenModal} size="small">
          New Event
        </Button>
      </Stack>
    </Stack>
  </>;
}
